import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Drawer
} from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import FilterPanel from './FilterPanel';
import EmptyState from './EmptyState';

const DataTableLayout = ({
  title,
  actionButtons = [],
  filterPanel,
  children,
  loading = false,
  isEmpty = false,
  emptyStateProps = {},
  mobileFilterOpen = false,
  onMobileFilterClose = () => {},
  sidebarWidth = 320
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const renderActionButtons = () => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {actionButtons.map((button, index) => (
        <Button
          key={index}
          variant={button.variant || 'contained'}
          startIcon={button.icon}
          onClick={button.onClick}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            py: 1,
            fontWeight: 600,
            boxShadow: button.variant === 'contained' ? 2 : 0,
            borderWidth: button.variant === 'outlined' ? 2 : 1,
            '&:hover': {
              boxShadow: button.variant === 'contained' ? 4 : 0,
              transform: 'translateY(-1px)',
              transition: 'all 0.2s'
            }
          }}
        >
          {button.label}
        </Button>
      ))}
    </Box>
  );

  const renderMobileActionButtons = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
      {actionButtons.map((button, index) => (
        <Button
          key={index}
          variant={button.variant || 'contained'}
          startIcon={button.icon}
          onClick={button.onClick}
          fullWidth
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            py: 1.5,
            fontWeight: 600
          }}
        >
          {button.label}
        </Button>
      ))}
    </Box>
  );

  return (
    <>
      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="left"
        open={mobileFilterOpen}
        onClose={onMobileFilterClose}
        sx={{ display: { md: 'none' } }}
      >
        <Box sx={{ width: 280, p: 2 }}>
          {filterPanel}
        </Box>
      </Drawer>

      <Box sx={{ 
        p: 0, 
        width: '100%', 
        height: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        position: 'relative'
      }}>
        {/* Desktop Layout */}
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' }, 
          gap: 3,
          width: '100%',
          flex: 1,
          minHeight: 0,
          pl: 3, // Left padding for spacing
          pr: 3, // Right padding
          pt: 3, // Top padding
          pb: 3, // Bottom padding
          boxSizing: 'border-box',
          margin: 0 // Ensure no margin
        }}>
          {/* Left Panel - Filters */}
          <Box sx={{ width: sidebarWidth, flexShrink: 0 }}>
            {filterPanel}
          </Box>

          {/* Right Panel - Main Content */}
          <Box sx={{ flexGrow: 1, minWidth: 0, width: 0, display: 'flex', flexDirection: 'column' }}> {/* width: 0 helps with flexbox calculation */}
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              }}
            >
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3 
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {title}
                </Typography>
                {renderActionButtons()}
              </Box>

              {/* Content */}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : isEmpty ? (
                <EmptyState {...emptyStateProps} />
              ) : (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {children}
                </Box>
              )}
            </Paper>
          </Box>
        </Box>

        {/* Mobile Layout */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              borderRadius: 3,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            {/* Mobile Header */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2 
            }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {title}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={onMobileFilterClose}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none'
                }}
              >
                Filters
              </Button>
            </Box>

            {/* Mobile Action Buttons */}
            {renderMobileActionButtons()}

            {/* Mobile Content */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : isEmpty ? (
              <EmptyState {...emptyStateProps} />
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                {children}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default DataTableLayout; 